import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ProgrammeModel } from '@/models/programme.model'
import { programmesService } from '@/services/programmesService'

const programmeKeys = {
  all: ['programmes'] as const,
  byIds: (ids: string[]) => [...programmeKeys.all, 'byIds', ...ids] as const,
}

export function useProgrammesByIds(programmeIds: string[] | undefined) {
  const sanitizedIds = useMemo(() => {
    if (!programmeIds || programmeIds.length === 0) return []
    const unique = Array.from(new Set(programmeIds.filter((id): id is string => Boolean(id))))
    return unique.sort()
  }, [programmeIds?.join(',') ?? ''])

  return useQuery({
    queryKey: programmeKeys.byIds(sanitizedIds),
    queryFn: async (): Promise<Record<string, ProgrammeModel>> => {
      if (sanitizedIds.length === 0) return {}
      const programmes = await Promise.all(
        sanitizedIds.map(async (id) => {
          try {
            return await programmesService.getProgramme(id)
          } catch {
            return null
          }
        }),
      )

      return programmes.reduce<Record<string, ProgrammeModel>>((acc, programme) => {
        if (programme?.id) {
          acc[programme.id] = programme
        }
        return acc
      }, {})
    },
    enabled: sanitizedIds.length > 0,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}
